import React, { useMemo } from 'react';
import { FeatureFlags, Filter } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { addCustomFiltersAsExpression, cleanEmptyFilters, filterToExpression } from '@utils/filterUtils';
import { useAvContext } from '../../../context/AvContextProvider';
import useQueryObject from '../../../hooks/useQueryObjectSql';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import {
  PieBasicLegendPosition,
  PieCategoryWidgetTypes,
  PieLegendStyles,
  PieSubType,
  SortDir,
  WidgetCategory,
} from '../../CustomDashboards/types';
import { filtersFieldToIgnore, USER_RISK_SCORE } from '../ticket.types';
import RiskScoreSeverityWidget from './RiskScoreSeverityWidget';
import { IEntityViewChartsProps } from './types';
import { entityViewWidgetStyle, EntityViewWidgetWrapper } from './utils';

const getIdentitiesByUserTypeWidget = ({
  filters,
  isSmallScreen,
}: {
  filters: Filter;
  isSmallScreen: boolean;
}): PieCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: { title: 'Identity Type' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [
    {
      filter: filters && Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [{ name: 'user.identity_type' }],
        metrics: [{ name: 'total_users_granular', alias: 'total_users_granular' }],
      },
      groupBy: ['user.identity_type'],
      sorting: [{ name: 'total_users_granular', dir: SortDir.DESC }],
      top: { size: 100, groupOthers: false },
      projectionId: { name: 'uber_users', builtIn: false },
      distinct: false,
      metricFilterRootProj: true,
    },
  ],
});

const getIdentitiesByExternalWidget = ({
  filters,
  isSmallScreen,
}: {
  filters: Filter;
  isSmallScreen: boolean;
}): PieCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: { title: 'External vs. Internal Users' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [
    {
      filter: filters && Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [{ name: 'user.type' }],
        metrics: [{ name: 'total_users_granular', alias: 'total_users_granular' }],
      },
      groupBy: ['user.type'],
      sorting: [{ name: 'total_users_granular', dir: SortDir.DESC }],
      top: { size: 3, groupOthers: true },
      projectionId: { name: 'uber_users', builtIn: false },
      distinct: false,
      metricFilterRootProj: true,
    },
  ],
});

const USER_SEVERITY_FIELD = 'user.severity';

const getUsersBySeverityWidget = ({ filters, isSmallScreen }: { filters: Filter; isSmallScreen: boolean }): PieCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: { title: 'Users by Severity' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [
    {
      filter: filters && Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [{ name: USER_SEVERITY_FIELD }],
        metrics: [{ name: 'total_users_granular', alias: 'total_users_granular' }],
      },
      groupBy: [USER_SEVERITY_FIELD],
      sorting: [],
      top: { size: 100, groupOthers: false },
      projectionId: { name: 'uber_users', builtIn: false },
      distinct: false,
      metricFilterRootProj: true,
    },
  ],
});

const IdentitiesIdentityViewCharts: React.FC<IEntityViewChartsProps> = props => {
  const { filters: f, isExpanded = true, setIsExpanded, searchFields } = props;
  const { measurements, featureFlags } = useAvContext();
  const isNewRiskScoreEnabled = !!featureFlags[FeatureFlags.NewRiskScore];
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1501));
  const { fieldTypeMap } = useAvContext().accountEntities;
  const filteredFilters = Object.keys(f).reduce(
    (acc, field) =>
      !filtersFieldToIgnore.includes(field) && !!f[field].length
        ? {
            ...acc,
            [field]: f[field],
          }
        : acc,
    {}
  );

  const measurementsTypeMap = measurements.visible.reduce(
    (acc, { systemName, type }) => ({
      ...acc,
      [systemName]: type,
    }),
    {}
  );
  const regularFilters = filterToExpression({ ...fieldTypeMap, ...measurementsTypeMap }, filteredFilters);

  const filters = addCustomFiltersAsExpression({
    expressionFiltersObj: cleanEmptyFilters({ and: { operands: [regularFilters] } }),
    operand: 'or',
    operator: 'contains',
    fields: searchFields,
    value: f?.search?.[0],
    fieldTypeMap,
  });

  const widgetsToShow = [
    getIdentitiesByUserTypeWidget({ filters, isSmallScreen }),
    getIdentitiesByExternalWidget({
      filters,
      isSmallScreen,
    }),
  ];
  const usersBySeverityWidget = getUsersBySeverityWidget({ filters, isSmallScreen });

  const { data: overviewUserType, isLoading: overviewUserTypeLoading } = useQueryObject({
    queryObject: widgetsToShow[0].requests[0],
    options: { placeholderData: keepPreviousData, gcTime: 0 },
  });
  const { data: overviewUserExternal, isLoading: overviewUserExternalLoading } = useQueryObject({
    queryObject: widgetsToShow[1].requests[0],
    options: { placeholderData: keepPreviousData, gcTime: 0 },
  });

  const summaryItems = useMemo(
    () => [
      {
        title: 'User Type',
        data: overviewUserType,
        isLoading: overviewUserTypeLoading,
        showIcon: false,
        onSuccess: data =>
          summarizeOnSuccess(data, widgetsToShow[0].requests[0].select.metrics[0].name, widgetsToShow[0].requests[0].select.dims[0].name),
      },
      {
        title: 'External vs. Internal Users',
        data: overviewUserExternal,
        isLoading: overviewUserExternalLoading,
        showIcon: false,
        onSuccess: data =>
          summarizeOnSuccess(data, widgetsToShow[1].requests[0].select.metrics[0].name, widgetsToShow[1].requests[0].select.dims[0].name),
      },
    ],
    [overviewUserType, overviewUserTypeLoading, overviewUserExternal, overviewUserExternalLoading]
  );

  const summarizeOnSuccess = (data, dataKey, titleKey) =>
    data?.reduce(
      (acc, curr) => ({
        ...acc,
        [curr[titleKey]]: curr[dataKey],
      }),
      {}
    );

  return (
    <EntityViewWidgetWrapper isExpanded={isExpanded} setIsExpanded={setIsExpanded} summaryItemsProps={{ items: summaryItems }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ ...flex.row, gap: 1.5, height: '208px' }}>
          {widgetsToShow.map(widget => (
            <Box
              key={widget.definition.title.title}
              sx={{
                ...entityViewWidgetStyle,
                position: 'relative',
                backgroundColor: theme => theme.palette.white.main,
              }}>
              <DraggableWidget
                widget={widget}
                customToolbar={
                  <Typography sx={{ whiteSpace: 'nowrap', marginBottom: '8px' }} variant="h6">
                    {widget.definition.title.title}
                  </Typography>
                }>
                {renderWidget({ widget })}
              </DraggableWidget>
            </Box>
          ))}
          {isNewRiskScoreEnabled && (
            <Box
              sx={{
                ...entityViewWidgetStyle,
                position: 'relative',
                backgroundColor: theme => theme.palette.white.main,
              }}>
              <RiskScoreSeverityWidget
                widget={usersBySeverityWidget}
                riskScoreField={USER_RISK_SCORE}
                severityField={USER_SEVERITY_FIELD}
                hasStoredSeverity={false}
              />
            </Box>
          )}
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default IdentitiesIdentityViewCharts;
